"use client";

import * as React from "react";

export type RegisterStep = {
    key: string;
    label: string;
    title?: string;
    description?: string;
    footnote?: boolean;
};

export type RegisterStepperApi = {
    activeKey: string;
    activeIndex: number;
    total: number;
    goTo: (key: string) => void;
    next: () => void;
    prev: () => void;
};

type RegisterStepperProviderProps = {
    steps: RegisterStep[];
    initialStepKey?: string;
    onStepChange?: (key: string) => void;
    children: React.ReactNode;
};

const RegisterStepperContext = React.createContext<RegisterStepperApi | null>(null);

export function useRegisterStepper() {
    const ctx = React.useContext(RegisterStepperContext);
    if (!ctx) {
        throw new Error("useRegisterStepper must be used within RegisterStepperProvider");
    }
    return ctx;
}

export function RegisterStepperProvider({
    steps,
    initialStepKey,
    onStepChange,
    children,
}: RegisterStepperProviderProps) {
    const [activeKey, setActiveKey] = React.useState<string>(() => {
        if (initialStepKey && steps.some((s) => s.key === initialStepKey)) return initialStepKey;
        return steps[0]?.key ?? "step";
    });

    React.useEffect(() => {
        if (steps.some((s) => s.key === activeKey)) return;
        const fallback = steps[0]?.key;
        if (fallback) setActiveKey(fallback);
    }, [activeKey, steps]);

    const activeIndex = React.useMemo(() => {
        const idx = steps.findIndex((s) => s.key === activeKey);
        return idx === -1 ? 0 : idx;
    }, [activeKey, steps]);

    const goTo = React.useCallback(
        (key: string) => {
            if (!steps.some((s) => s.key === key)) return;
            setActiveKey(key);
            onStepChange?.(key);
        },
        [onStepChange, steps]
    );

    const next = React.useCallback(() => {
        const target = steps[activeIndex + 1]?.key;
        if (target) goTo(target);
    }, [activeIndex, goTo, steps]);

    const prev = React.useCallback(() => {
        const target = steps[activeIndex - 1]?.key;
        if (target) goTo(target);
    }, [activeIndex, goTo, steps]);

    const total = Math.max(steps.length, 1);

    const api: RegisterStepperApi = React.useMemo(
        () => ({
            activeKey,
            activeIndex,
            total,
            goTo,
            next,
            prev,
        }),
        [activeIndex, activeKey, goTo, next, prev, total]
    );

    return <RegisterStepperContext.Provider value={api}>{children}</RegisterStepperContext.Provider>;
}
